/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AlgorithmOptions,
  DefaultRenderer,
  type Marker,
  MarkerClusterer,
  MarkerClustererOptions,
} from '@googlemaps/markerclusterer';
import { MapIcon, ModuleConfig } from '@schamburg-web/interfaces';
import { useMap } from '@vis.gl/react-google-maps';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useModuleData } from '../../util/useModuleData';
import { validLatLng } from '../../util/MapUtil';
import { GoogleClusteredMarker } from './GoogleClusteredMarker';
import _ from 'lodash';
import { useRef } from 'react';

const markerClustererOptions: MarkerClustererOptions = {
  algorithmOptions: {
    maxZoom: 12,
    radius: 80,
  } as AlgorithmOptions,
  renderer: new DefaultRenderer(),
};

const GoogleClusterLayer: React.FC<{
  module: ModuleConfig;
}> = ({ module }) => {
  const lastClustererRef = useRef<MarkerClusterer | null>(null);
  // Use the isolated module data hook - this will only re-render when THIS module's data changes
  const { displayObjects, isEnabled } = useModuleData(module.name);
  const [icons, setIcons] = useState<MapIcon[]>([]);

  useEffect(() => {
    if (!isEnabled) {
      setIcons([]);
      return;
    }

    const filteredObjects = displayObjects.filter((obj: any) =>
      validLatLng(obj)
    );
    setIcons((prevIcons) => {
      if (_.isEqual(prevIcons, filteredObjects)) {
        return prevIcons;
      }
      return filteredObjects;
    });
  }, [displayObjects, isEnabled, module.name]);

  const [markers, setMarkers] = useState<{ [key: string]: Marker }>({}); // create the markerClusterer once the map is available and update it when
  // the markers are changed
  const map = useMap();
  const clusterer = useMemo(() => {
    if (!map || Object.keys(markers).length === 0) return null;
    // Use module.mapConfig.clusterLayerOptions if present, otherwise default
    let options: MarkerClustererOptions = { ...markerClustererOptions };
    if (module?.mapConfig?.clusterLayerOptions) {
      // Copy all except renderer
      const { renderer, ...rest } = module.mapConfig.clusterLayerOptions;
      options = { ...markerClustererOptions, ...rest };
      if (renderer) {
        if (typeof renderer === 'function') {
          options.renderer = { render: renderer };
        } else {
          options.renderer = renderer;
        }
      }
    }
    return new MarkerClusterer({
      map,
      ...options,
    });
  }, [map, module, markers]);

  // Cleanup MarkerClusterer when component unmounts
  useEffect(() => {
    // Clean up the previous clusterer if it exists and is different
    if (lastClustererRef.current && lastClustererRef.current !== clusterer) {
      lastClustererRef.current.clearMarkers();
      lastClustererRef.current.setMap(null);
    }
    lastClustererRef.current = clusterer;

    // Clean up on unmount
    return () => {
      if (clusterer) {
        clusterer.clearMarkers();
        clusterer.setMap(null);
      }
    };
  }, [clusterer]);

  useEffect(() => {
    if (!clusterer) return;

    clusterer.clearMarkers();
    clusterer.addMarkers(Object.values(markers));
  }, [clusterer, markers, module.name]);

  // this callback will effectively get passsed as ref to the markers to keep
  // tracks of markers currently on the map
  const setMarkerRef = useCallback((marker: Marker | null, key: string) => {
    setMarkers((prevMarkers) => {
      if ((marker && prevMarkers[key]) || (!marker && !prevMarkers[key]))
        return prevMarkers;

      if (marker) {
        return { ...prevMarkers, [key]: marker };
      } else {
        const { [key]: _, ...newMarkers } = prevMarkers;
        return newMarkers;
      }
    });
  }, []);

  if (icons.length > 0) {
    return (
      <>
        {icons.map((obj: MapIcon) => (
          <GoogleClusteredMarker
            iconObj={obj}
            module={module}
            key={obj.id}
            setMarkerRef={setMarkerRef}
          />
        ))}
      </>
    );
  } else {
    return null;
  }
};

export default GoogleClusterLayer;
